import React, { useState } from 'react';
import { useQuery } from '@common/hooks';
import { ORGANIZATION_UNITS_QUERY } from '@institutions/graphql';
import { ErrorHandler, TableTransfer } from '@common/components';
import { useSelectedOrganizationUnits } from './_rates-long-term-context';
import { TransferItem } from 'antd/lib/transfer';

const { filterOptionForColumns } = TableTransfer;

type TableRowDataType = {
  id: string;
  key: string;
  unitName: string;
  unit: string;
  costCenter: string;
  facility: string;
} & TransferItem;

type SortParameterType = keyof TableRowDataType;

// Updated type to match the actual query response
type OrganizationUnitFromQueryType = {
  id: string;
  name: string;
  costCenter: string;
  department: {
    id: string;
    organizationBusiness: {
      id: string;
      name: string;
    };
  };
  clinicalUnit: {
    id: string;
    displayName: string;
  };
};

type OrganizationUnitsQueryResponseType = {
  organizationUnits: OrganizationUnitFromQueryType[];
};

const tableColumns = [
  {
    dataIndex: 'unitName',
    key: 'unitName',
    title: 'Unit Name',
    sorter: getSortCallbackBy('unitName'),
  },
  {
    dataIndex: 'unit',
    key: 'unit',
    title: 'Unit',
    sorter: getSortCallbackBy('unit'),
  },
  {
    dataIndex: 'costCenter',
    key: 'costCenter',
    title: 'Cost Center',
    sorter: getSortCallbackBy('costCenter'),
  },
  {
    dataIndex: 'facility',
    key: 'facility',
    title: 'Facility',
    sorter: getSortCallbackBy('facility'),
  },
];

export function RatesLongTermStepUnits() {
  const [selectedOrganizationUnits, setSelectedOrganizationUnits] =
    useSelectedOrganizationUnits();
  const [targetKeys, setTargetKeys] = useState<string[]>(
    selectedOrganizationUnits.map((unit) => unit.id)
  );
  const { loading, error, data } = useQuery<OrganizationUnitsQueryResponseType>(
    ORGANIZATION_UNITS_QUERY,
    {
      fetchPolicy: 'cache-first',
    }
  );

  if (error) {
    return <ErrorHandler error={error} log="RatesStepsStepOne" />;
  }

  function onChange(newTargetKeys: string[]) {
    setTargetKeys(newTargetKeys);
    setSelectedOrganizationUnits(
      data?.organizationUnits
        .filter((unit: OrganizationUnitFromQueryType) =>
          newTargetKeys.includes(unit.id)
        )
        .map((unit: OrganizationUnitFromQueryType) => ({
          id: unit.id,
          businessId: unit.department.organizationBusiness.id,
          clinicalId: unit.clinicalUnit.id,
        })) ?? []
    );
  }

  const filterOption = filterOptionForColumns(
    tableColumns.map(({ key }) => key)
  );

  return (
    <TableTransfer<TableRowDataType>
      loading={loading}
      className="rates-steps__step-one"
      dataSource={normalizeData(data)}
      targetKeys={targetKeys}
      disabled={false}
      onChange={onChange}
      columns={tableColumns}
      showSearch
      filterOption={filterOption}
    />
  );
}

function getSortCallbackBy(param: SortParameterType) {
  return (a: TableRowDataType, b: TableRowDataType) => {
    if (a[param] === b[param]) {
      return 0;
    }

    return a[param] > b[param] ? 1 : -1;
  };
}

function normalizeData(
  data?: OrganizationUnitsQueryResponseType
): TableRowDataType[] {
  return (
    data?.organizationUnits?.map(
      (organizationUnit: OrganizationUnitFromQueryType) => ({
        id: organizationUnit.id,
        key: organizationUnit.id,
        unitName: organizationUnit.name,
        unit: organizationUnit.clinicalUnit.displayName,
        costCenter: organizationUnit.costCenter,
        facility: organizationUnit.department.organizationBusiness.name,
      })
    ) ?? []
  );
}
